import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  IconButton,
  Radio,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { mutateProfile } from 'hooks';
import { throttle } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useEffect, useState } from 'react';
import { useOrganizationsApi } from 'services/api';

const TEXT_COLOR = '#383838';
const BORDER_SELECTED = '#23305F';
const BORDER_DEFAULT = '#DEDEDE';
const CONFIRM_BG = '#9B0000';

function RadioOption ({
  selected,
  onClick,
  label,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
}): ReactElement {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        height: 60,
        px: '14px',
        py: '13px',
        borderRadius: '10px',
        border: selected
          ? `1px solid ${BORDER_SELECTED}`
          : `1.5px solid ${BORDER_DEFAULT}`,
        backgroundColor: 'white',
        cursor: 'pointer',
        width: '100%',
        overflow: 'hidden',
        textAlign: 'left',
      }}
    >
      <Radio
        checked={selected}
        size="small"
        sx={{
          p: 0,
          color: selected ? BORDER_SELECTED : BORDER_DEFAULT,
          '&.Mui-checked': { color: BORDER_SELECTED },
        }}
        tabIndex={-1}
      />
      <Typography
        sx={{
          fontFamily: '\'Poppins\', sans-serif',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '21px',
          color: TEXT_COLOR,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export interface RemoveLeaderModalProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  userId: string;
  leaderDisplayName?: string;
  leaderOrgRole?: string;
  onSuccess?: () => Promise<void>;
}

function ModalContent ({
  title,
  removeMode,
  setRemoveMode,
  isSubmitting,
  onSubmit,
  onClose,
  leaderDisplayName,
}: {
  title: string;
  removeMode: 'member' | 'org';
  setRemoveMode: (v: 'member' | 'org') => void;
  isSubmitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  leaderDisplayName?: string;
}): ReactElement {
  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        alignItems: 'flex-start',
        pt: '14px',
        px: '20px',
        pb: '36px',
        backgroundColor: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Close (X) */}
      <Box display="flex" justifyContent="flex-end" width="100%">
        <IconButton onClick={onClose} size="small" sx={{ p: 0 }}>
          <CloseIcon sx={{ fontSize: 19, color: TEXT_COLOR }} />
        </IconButton>
      </Box>

      {/* Title */}
      <Typography
        sx={{
          fontFamily: '\'Poppins\', sans-serif',
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '24px',
          color: TEXT_COLOR,
        }}
      >
        {title}
      </Typography>

      {/* Subtitle */}
      {leaderDisplayName ? (
        <Typography
          sx={{
            fontFamily: '\'Poppins\', sans-serif',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '18px',
            color: TEXT_COLOR,
          }}
        >
          What would you like to do with their membership?
        </Typography>
      ) : (
        <Typography
          sx={{
            fontFamily: '\'Poppins\', sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '21px',
            color: TEXT_COLOR,
          }}
        >
          The user will no longer have the ability to submit and manage events from this organization.
        </Typography>
      )}

      {/* Radio options */}
      {leaderDisplayName ? (
        <Box display="flex" flexDirection="column" gap="10px" width="100%" overflow="hidden">
          <RadioOption
            selected={removeMode === 'member'}
            onClick={() => setRemoveMode('member')}
            label="Keep as a general member"
          />
          <RadioOption
            selected={removeMode === 'org'}
            onClick={() => setRemoveMode('org')}
            label="Remove from organization"
          />
        </Box>
      ) : null}

      {/* Action buttons */}
      <Box display="flex" gap="10px" alignItems="flex-start" width="100%" overflow="hidden">
        {/* Cancel */}
        <Box
          component="button"
          type="button"
          onClick={onClose}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: '13px',
            borderRadius: '10px',
            border: `1px solid ${BORDER_DEFAULT}`,
            backgroundColor: 'white',
            cursor: 'pointer',
            overflow: 'hidden',
          }}
        >
          <Typography
            sx={{
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: 'normal',
              color: TEXT_COLOR,
              textDecoration: 'underline',
              whiteSpace: 'nowrap',
            }}
          >
            Cancel
          </Typography>
        </Box>

        {/* Confirm */}
        <Box
          component="button"
          type="submit"
          disabled={isSubmitting}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: '13px',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: CONFIRM_BG,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.7 : 1,
            overflow: 'hidden',
          }}
        >
          <Typography
            sx={{
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: 'normal',
              color: 'white',
              whiteSpace: 'nowrap',
            }}
          >
            Confirm
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export const RemoveLeaderModal = ({
  open,
  organizationId,
  userId,
  leaderDisplayName,
  leaderOrgRole,
  onClose,
  onSuccess,
}: RemoveLeaderModalProps): ReactElement => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [removeMode, setRemoveMode] = useState<'member' | 'org'>('member');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      setRemoveMode('member');
    }
  }, [open]);

  const organizationsApi = useOrganizationsApi();
  const { enqueueSnackbar } = useSnackbar();

  const title =
    leaderDisplayName && leaderOrgRole
      ? `Remove ${leaderDisplayName} as ${leaderOrgRole}?`
      : 'Remove this user from the organization?';

  const onSubmit = throttle(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);

      const result = await organizationsApi.removeLeaderFromOrganization(
        organizationId,
        userId,
        { removeFromOrganization: removeMode === 'org' },
      );

      void mutateProfile();

      if (result.success) {
        const successLine =
          leaderDisplayName && leaderOrgRole
            ? `${leaderDisplayName} removed as ${leaderOrgRole}`
            : 'Leader removed successfully';
        enqueueSnackbar(successLine, { variant: 'success' });
        if (onSuccess) await onSuccess();
        return onClose?.();
      }

      setSubmitting(false);
      enqueueSnackbar(result.error.message, { variant: 'error' });
    },
    200,
    { leading: true },
  );

  const content = (
    <ModalContent
      title={title}
      removeMode={removeMode}
      setRemoveMode={setRemoveMode}
      isSubmitting={isSubmitting}
      onSubmit={onSubmit}
      onClose={onClose}
      leaderDisplayName={leaderDisplayName}
    />
  );

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => undefined}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            overflow: 'hidden',
          },
        }}
      >
        {content}
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'escapeKeyDown' || reason === 'backdropClick') onClose();
      }}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxWidth: 400,
          width: '100%',
          overflow: 'hidden',
        },
      }}
    >
      {content}
    </Dialog>
  );
};
